import { FlakinessReport } from "@flakiness/flakiness-report";
import { WireTypes } from "@flakiness/server/common/wireTypes.js";
import { html, nothing, TemplateResult } from "lit";
import ms from "ms";

export function renderCategoryIcon(cat: string, options: {
  slot?: string,
  klass?: string,
} = {}) {
  const { slot = nothing, klass = nothing } = options;
  if (cat === 'vitest')
    return html`<sl-icon name=lightning-charge class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'cypress')
    return html`<sl-icon src="/cypress-bw.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'jest')
    return html`<sl-icon src="/jest.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'pytest')
    return html`<sl-icon src="/python.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === FlakinessReport.CATEGORY_JUNIT)
    return html`<sl-icon name='filetype-xml' class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'cucumberjs')
    return html`<sl-icon src="/cucumber.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'bun')
    return html`<sl-icon src="/bun-bw.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'rust')
    return html`<sl-icon src="/rust-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'go')
    return html`<sl-icon src="/go.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'node' || cat === 'node:test')
    return html`<sl-icon src="/nodejs-bw.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'xcuitest')
    return html`<sl-icon src="/xcode-bw.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'perf')
    return html`<sl-icon name='speedometer' class=${klass} slot=${slot}></sl-icon>`;
  if (cat === FlakinessReport.CATEGORY_PLAYWRIGHT)
    return html`<sl-icon src='/masks.svg' class=${klass} slot=${slot}></sl-icon>`;
  return html`<sl-icon name=play-circle class=${klass} slot=${slot}></sl-icon>`;
}

export function renderCategoryIconColor(cat: string, options: {
  slot?: string,
  klass?: string,
} = {}) {
  const { slot = nothing, klass = nothing } = options;
  if (cat === 'cypress')
    return html`<sl-icon src="/cypress-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'vitest')
    return html`<sl-icon src="/vitest-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'jest')
    return html`<sl-icon src="/jest-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'pytest')
    return html`<sl-icon src="/python-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === FlakinessReport.CATEGORY_JUNIT)
    return html`<sl-icon src="/junitxml-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'cucumberjs')
    return html`<sl-icon src="/cucumber-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'bun')
    return html`<sl-icon src="/bun-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'rust')
    return html`<sl-icon src="/rust-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'go')
    return html`<sl-icon src="/go-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'node' || cat === 'node:test')
    return html`<sl-icon src="/nodejs-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'xcuitest')
    return html`<sl-icon src="/xcode-color.svg" class=${klass} slot=${slot}></sl-icon>`;
  if (cat === 'perf')
    return html`<sl-icon name='speedometer' class=${klass} slot=${slot}></sl-icon>`;
  if (cat === FlakinessReport.CATEGORY_PLAYWRIGHT)
    return html`<sl-icon src='/playwright-color.svg' class=${klass} slot=${slot}></sl-icon>`;
  return html`<sl-icon name=play-circle class=${klass} slot=${slot}></sl-icon>`;
}

export function renderDate(timestampMs: number, options?: {
  seconds?: boolean
}) {
  return html`
    <sl-format-date month="short" day="numeric" year="numeric" hour="numeric" minute="numeric" second=${options?.seconds ? 'numeric' : nothing} date=${new Date(timestampMs).toISOString()}></sl-format-date>
  `;
}


export function renderRelativeDate(timestampMs: number) {
  const date = new Date(timestampMs).toISOString();
  return html`
    <sl-tooltip hoist>
      <sl-format-date slot=content month="short" day="numeric" year="numeric" hour="numeric" minute="numeric" date=${date}></sl-format-date>
      <sl-relative-time date=${date}></sl-relative-time>
    </sl-tooltip>
  `;
}

// Recent uploads read better as "5 minutes ago", older ones as a date.
export function renderLastUpload(timestampMs: number|undefined) {
  if (!timestampMs)
    return html`No uploads`;
  if (Date.now() - ms('1 day') < timestampMs)
    return html`Updated ${renderRelativeDate(timestampMs)}`;
  return html`Updated on <sl-format-date month="long" day="numeric" year="numeric" date=${new Date(timestampMs).toISOString()}></sl-format-date>`;
}

/**
 * Data snapshots this server instance is holding. The project count is worth showing
 * next to the total: snapshots are capped per project, so the total on its own does
 * not say whether one project is churning or many are being explored at once.
 */
export function renderSnapshots(serverInfo?: WireTypes.ServerInfo) {
  if (!serverInfo)
    return nothing;
  const { total, projects } = serverInfo.snapshots;
  return html`<strong>${total}</strong> (${projects} project${projects === 1 ? '' : 's'})`;
}

export function iconify(iconName: string|typeof nothing, content: TemplateResult<1>) {
  return html`
    <h-box style="gap: var(--sl-spacing-2x-small);">
      <sl-icon name=${iconName}></sl-icon>
      ${content}
    </h-box>
  `;
}

export function renderDotSeparated(elements: TemplateResult<1>[]) {
  const all = elements.map(e => [
    e,
    html`<sl-icon name=dot></sl-icon>`,
  ]).flat();
  all.pop();
  return all;
}
